import { useState, useRef, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  Typography as MuiTypography,
  Tooltip,
} from "../../../common/utils/mui-imports";
import {
  ArrowRight2,
  ClipboardText,
  ArrowBackIosNewOutlinedIcon,
} from "../../../common/utils/mui-imports";
import { Splitter } from "../../../common/utils/antd-imports";
import HPRTextBox from "../../../components/common/HPRTextBox/HPRTextBox";
import { EnumTextBoxType } from "../../../components/common/HPRTextBox/HPRTextBox.enum";
import HPRTextBoxAutoResize from "../../../components/common/HPRTextBox/HPRTextBoxAutoResize";
import CopyDetailsPopup from "../../../components/Popups/CopyDetailsPopup/CopyDetailsPopup";
import MultipleSelectorWithSearch from "../../../components/common/MultipleSelectorWithSearch/MultipleSelectorWithSearch";
import QuestionsTable from "../../components/ReviewQuestions/QuestionsTable";
import HPRFileUploaderWithNavigation from "../../../components/common/HPRFileUploaderWithNavigation/HPRFileUploaderWithNavigation";
import {
  DefaultTextDTO,
  DropDownDTO,
  QuestionChoiceDTO,
  QuestionDTO,
  StandardQuestionBulkUploadDTO,
  StandardQuestionUploadDTO,
  UploadFileDTO,
} from "../../../common/types/common.dto.types";
import {
  EnumFileType,
  EnumQuestionTypesType,
  EnumStatus,
} from "../../../common/enums/common.enums";
import { useAppDispatch, useAppSelector } from "../../../core/hooks";
import {
  bulkUploadStandardQuestion,
  getBulkUploadQuestionCategoryChoices,
  getBulkUploadQuestionDropdownCategories,
  getBulkUploadQuestionTypeDropdown,
  getRowDataOnClick,
  notifyStandardQuestionIssuesByEmail,
  saveStandardQuestionBulkUpload,
} from "./BulkUploadQuestionsPage.action";
import {
  BulkQuestionUploadPageParametersType,
  BulkQuestionUploadPageType,
  EnumBulkQuestionUploadPageMode,
} from "./BulkUploadQuestionsPage.types";
import {
  resetBulkQuestionUploadState,
  setBulkQuestionUploadInitialData,
  setBulkUploadQuestionCategoryChoices,
  setBulkUploadQuestionDropdownCategories,
  setPageNavigation,
  setStandardQuestionReviewList,
  setUploadedFileName,
  showCategoryChoicesValidationMsg,
} from "./BulkUploadQuestionsPage.reducer";
import { PageRegistrationProperties } from "../../../core/reducers/pageRegistrationReducer.types";
import usePageRegistration from "../../../core/hooks/usePageRegistration";
import HPRDropDownSingleSelect from "../../../components/common/HPRDropDownSingleSelect/HPRDropDownSingleSelect";
import { StandardQuestionsPageParametersType } from "../StandardQuestionsPage/StandardsQuestionsPage.types";
import {
  booleanToNumber,
  findIndexOfSelectionChange,
  isObjectEqualTo,
  parseToBoolean,
} from "../../../common/functions/common.functions";
import SimpleReactValidator from "simple-react-validator";
import React from "react";
import { openPopup } from "../../../components/Popups/Popup.reducer";
import { EnumPopupType } from "../../../components/Popups/Popup.enum";
import { PopupProps } from "../../../components/Popups/PopupProps.types";
import { UsePromptTypes } from "../../../core/hooks/use-prompt/use-prompt.types";
import { usePrompt } from "../../../core/hooks/use-prompt/use-prompt";
import { ArrowCircleRight, CloseCircle, InfoCircle } from "iconsax-react";
import BulkUploadValidateQuestion from "../../components/BulkUploadValidateQuestion/BulkUploadValidateQuestion";
import { BulkUploadQuestionCountType } from "../../components/BulkUploadValidateQuestion/BulkUploadValidateQuestion.types";

const BulkStandardQuestionUploader = (props: PageRegistrationProperties) => {
  usePageRegistration(props);

  // Initial state values for the Bulk Question Upload Page.
  const initialState = {
    StandardId: 0,
    UploadedFileName: "",
    StandardQuestionReviewList: [] as Array<StandardQuestionUploadDTO>,
    Validator: new SimpleReactValidator({
      messages: {
        default: "This field is required",
      },
    }),
    BulkQuestionUploadPageMode:
      EnumBulkQuestionUploadPageMode.UploadQuestionMode,
    ShowCategoryChoicesValidationMsg: false,
    PageNavigation: { EnablePageNavigation: false, NavigationPage: "" },
  } as BulkQuestionUploadPageType;

  // Initialize Redux dispatch
  const dispatch = useAppDispatch();

  // pass parameters from outside to page
  const location = useLocation();
  const parameter: BulkQuestionUploadPageParametersType = location.state;

  const navigate = useNavigate();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isDragOver, setIsDragOver] = useState(false);

  //Ref used to ensure initial data is only fetched once.
  const dataFetchedRef = useRef(false);

  // state slice from Redux that holds bulk upload data.
  const bulkQuestionUploadState = useAppSelector(
    (state) => state.bulkQuestionUploadReducer
  );

  //Manages visibility of the "Copy" popup modal.
  const [copySelectedQuestion, setCopySelectedQuestion] =
    useState<StandardQuestionUploadDTO>(null);
  const [copyPopupOpen, setCopyPopupOpen] = useState(false);

  //Stores the currently selected standard question.
  const [selectedStandardQuestion, setSelectedStandardQuestion] =
    useState<StandardQuestionUploadDTO>(null);

  // create state with initialState values
  const [
    bulkStandardQuestionUploaderPageState,
    setBulkStandardQuestionUploaderPageState,
  ] = useState({ ...initialState, StandardId: parameter.StandardId });

  // Creates a forceUpdate function that forces a component re-render
  const forceUpdate = React.useReducer((x) => x + 1, 0)[1];

  /**
   * Reassigns `RowId` values in the given list based on their position.
   * Each question is assigned a new code in the format "Q1", "Q2", ..., based on index
   */
  const reOrderStandardQuestionReviewList = (
    standardQuestionReviewList: Array<StandardQuestionUploadDTO>
  ): Array<StandardQuestionUploadDTO> => {
    // Return early if the list is empty or undefined
    if (standardQuestionReviewList?.length === 0) return [];

    // Return a new list with reassigned RowIds
    return [
      ...standardQuestionReviewList?.map(
        (question: StandardQuestionUploadDTO, index: number) => {
          const sequenceNumber = index + 1;
          return {
            ...question,
            RowId: `${sequenceNumber}`,
            SequenceNumber: sequenceNumber,
            IsSelected: false,
            DisplayId: question?.DisplayId,
          };
        }
      ),
    ];
  };

  /**
   * Retrieves the index of the currently selected standard question
   * from the bulk standard question review list.
   */
  const getSelectedStandardQuestionArrayIndex = (): number => {
    if (!selectedStandardQuestion) return -1;

    const { StandardQuestionReviewList: standardQuestionReviewList } =
      bulkStandardQuestionUploaderPageState;
    const { RowId: selectedRowId } = selectedStandardQuestion;

    // Find the index of the selected question in the review list and return
    return standardQuestionReviewList?.findIndex(
      (q) => q.RowId === selectedRowId
    );
  };

  /**
   * Update the page state with the new review list
   */
  const updateStandardQuestionReviewListState = (
    standardQuestionList: Array<StandardQuestionUploadDTO>
  ) => {
    setBulkStandardQuestionUploaderPageState((prevState) => {
      return {
        ...prevState,
        StandardQuestionReviewList: [...standardQuestionList],
      };
    });
  };

  /**
   * reset selected standard question
   */
  const resetSelectedStandardQuestionState = () => {
    const { StandardQuestionReviewList: standardQuestionReviewList } =
      bulkStandardQuestionUploaderPageState;

    const index = getSelectedStandardQuestionArrayIndex();
    if (index === -1) {
      setSelectedStandardQuestion(null);
      return;
    }

    setSelectedStandardQuestion({ ...standardQuestionReviewList[index] });
  };

  /**
   * show Success Popup
   */
  const showSuccessPopup = (props: {
    message: string;
    onConfirm?: () => void;
  }) => {
    dispatch(
      openPopup({
        Open: true,
        BodyText: props.message,
        HeaderText: "Success",
        PopupType: EnumPopupType.SuccessPopup,
      } as PopupProps)
    );
  };

  /**
   * show Invalid Question Warning opup
   */
  const showInvalidQuestionWarning = (props: {
    message: string;
    onConfirm?: () => void;
  }) => {
    dispatch(
      openPopup({
        Open: true,
        BodyText: props.message,
        HeaderText: "Warning",
        PopupType: EnumPopupType.WarningPopup,
        OkBtnClick() {
          props.onConfirm?.();
        },
      } as PopupProps)
    );
  };

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // Skip if no valid StandardId or if data is already fetched
    if (
      !parameter?.StandardId ||
      parameter?.StandardId <= 0 ||
      dataFetchedRef.current
    )
      return;
    // Mark data as fetched to prevent duplicate calls
    dataFetchedRef.current = true;

    // rset page state
    setBulkStandardQuestionUploaderPageState({
      ...initialState,
      StandardId: parameter.StandardId,
    });

    // Initialize bulk upload state with provided StandardId
    dispatch(
      setBulkQuestionUploadInitialData({
        ...initialState,
        StandardId: parameter.StandardId,
      })
    );

    // Fetch available question types for dropdown
    dispatch(getBulkUploadQuestionTypeDropdown());
  }, []);

  useEffect(() => {
    if (!bulkQuestionUploadState.PageNavigation?.EnablePageNavigation) return;

    dispatch(
      setPageNavigation({ EnablePageNavigation: false, NavigationPage: "" })
    );

    navigate(bulkQuestionUploadState.PageNavigation?.NavigationPage, {
      state: {
        StandardId: parameter.StandardId,
        StandardName: parameter.StandardName,
        EnumStatus: EnumStatus.Published,
      } as StandardQuestionsPageParametersType,
    });
  }, [bulkQuestionUploadState.PageNavigation]);

  //On reducer status change
  useEffect(() => {
    // Only run this effect after the initial data fetch has completed
    if (!dataFetchedRef.current) return;

    // Update uploader page state with new readiness flag and reordered list
    const updatedStandardQuestionList = [
      ...reOrderStandardQuestionReviewList(
        bulkQuestionUploadState.StandardQuestionReviewList
      ),
    ];
    setBulkStandardQuestionUploaderPageState((prevState) => {
      return {
        ...prevState,
        BulkQuestionUploadPageMode:
          bulkQuestionUploadState?.BulkQuestionUploadPageMode,
        StandardQuestionReviewList: [...updatedStandardQuestionList],
      };
    });

    resetSelectedStandardQuestionState();

    forceUpdate();
  }, [
    bulkQuestionUploadState.BulkQuestionUploadPageMode,
    bulkQuestionUploadState.StandardQuestionReviewList,
  ]);

  useEffect(() => {
    // Exit early if no question is currently selected
    if (!selectedStandardQuestion) return;

    const { BulkQuestionCategoryChoiceList } = bulkQuestionUploadState;
    const { QuestionChoices } = selectedStandardQuestion;

    const gapQuestionChoices = QuestionChoices?.filter(
      (filter) => filter.IsGap ?? false
    )?.map((choise) => {
      return choise.ChoiceTextId?.toString();
    });

    let updatedChoiceList = [...BulkQuestionCategoryChoiceList];

    const hasNewChoices = updatedChoiceList && updatedChoiceList?.length > 0;

    if (!hasNewChoices) return;

    if (gapQuestionChoices && gapQuestionChoices?.length > 0) {
      updatedChoiceList = [
        ...updatedChoiceList?.map((choise) => {
          const isGap =
            gapQuestionChoices &&
            gapQuestionChoices?.includes(choise.ChoiceTextId?.toString());

          return {
            ...choise,
            IsGap: isGap,
          };
        }),
      ];
    }

    // Update the selected question's choices with the latest category choice list
    setSelectedStandardQuestion((prevState) => {
      return {
        ...prevState,
        QuestionChoices: [...updatedChoiceList] ?? [],
      };
    });
  }, [bulkQuestionUploadState.BulkQuestionCategoryChoiceList]);

  /**
   * Validate the selected standard question's category and choices.
   */
  useEffect(() => {
    if (!bulkQuestionUploadState.ShowCategoryChoicesValidationMsg) return;

    dispatch(showCategoryChoicesValidationMsg(false));

    const index = getSelectedStandardQuestionArrayIndex();
    if (index === -1) return;

    const { StandardQuestionReviewList: standardQuestionReviewList } =
      bulkStandardQuestionUploaderPageState;

    const updatedStandardQuestionList = [...standardQuestionReviewList];

    showInvalidQuestionWarning({
      message:
        "This requirement's choice have been modified by another user. The selected choice and gap(s) will be reset. Please review and reselect the updated choices.",
      onConfirm() {
        updatedStandardQuestionList[index] = {
          ...updatedStandardQuestionList[index],
          QuestionCategory: 0,
          QuestionCategoryName: "",
          QuestionChoices: [],
        };

        setBulkStandardQuestionUploaderPageState((prevState) => ({
          ...prevState,
          StandardQuestionReviewList: [...updatedStandardQuestionList],
        }));

        setSelectedStandardQuestion({
          ...updatedStandardQuestionList[index],
        });
      },
    });
  }, [bulkQuestionUploadState.ShowCategoryChoicesValidationMsg]);

  /**
   * Normalizes a standard question object by ensuring all required fields are present
   */
  const normalizeStandardQuestion = (question) => {
    if (!question) return null;

    return {
      Requirement: question.Requirement ?? "",
      QuestionType: question.QuestionType ?? 0,
      QuestionCategory: question.QuestionCategory ?? 0,
      IncludeNotApplicable: question.IncludeNotApplicable ?? false,
      AllowComments: question.AllowComments ?? false,
      QuestionChoices:
        question.QuestionChoices?.map((choice) => ({
          ChoiceTextId: choice.ChoiceTextId,
          IsGap: choice.IsGap,
        })) ?? [],
    };
  };

  /**
   * Checks whether the currently selected standard question has unsaved changes
   */
  const isSelectedQuestionIsDirty = () => {
    const { StandardQuestionReviewList: standardQuestionList } =
      bulkStandardQuestionUploaderPageState;

    // Get the index of the selected question in the review list
    const index = getSelectedStandardQuestionArrayIndex();

    // Determine if the selected question exists in the list
    if (index === -1 || !selectedStandardQuestion) {
      return false;
    }

    // Normalize the original question from the list
    const originalStandardQuestion = normalizeStandardQuestion(
      standardQuestionList[index]
    );

    // Normalize the current selected question for comparison
    const selectedQuestionTemp = normalizeStandardQuestion(
      selectedStandardQuestion
    );

    // Compare both normalized objects for deep equality
    return !isObjectEqualTo(selectedQuestionTemp, originalStandardQuestion);
  };

  /**
   * Effect to update the `IsDirty` flag of the selected standard question in the review list.
   */
  useEffect(() => {
    const { StandardQuestionReviewList: standardQuestionList } =
      bulkStandardQuestionUploaderPageState;

    // Exit early if the review list is empty or undefined
    if (standardQuestionList?.length === 0) return;

    const { RowId: RowId } =
      selectedStandardQuestion ?? ({} as StandardQuestionUploadDTO);

    // Determine if the selected question exists in the list
    const isDirty = isSelectedQuestionIsDirty();

    // Create an updated list where only the matching question has IsDirty set to true
    const updatedStandardQuestionList = [
      ...standardQuestionList?.map((question) => {
        return {
          ...question,
          IsDirty: isDirty && question.RowId?.toString() === RowId?.toString(),
        };
      }),
    ];

    // Update the state with the new review list containing the updated IsDirty flag
    updateStandardQuestionReviewListState(updatedStandardQuestionList);

    if (
      selectedStandardQuestion &&
      selectedStandardQuestion?.IsDirty !== isDirty
    ) {
      // update selected question state
      setSelectedStandardQuestion((prevState) => {
        return { ...prevState, IsDirty: isDirty };
      });
    }
  }, [selectedStandardQuestion]);

  /**
   * Checks if the selected question's type is FreeText.
   */
  const isFreeTextQuestionSelected = (): boolean =>
    selectedStandardQuestion?.QuestionType === EnumQuestionTypesType.FreeText;

  /**
   * Checks if the selected question's type is YesNo.
   */
  const isYesNoQuestionSelected = (): boolean =>
    selectedStandardQuestion?.QuestionType === EnumQuestionTypesType.YesNo;

  /**
   * Checks if the disabled Question Category DropDown.
   */
  const isDisabledQuestionCategoryDropDown = (): boolean =>
    selectedStandardQuestion && !selectedStandardQuestion.QuestionType;

  /**
   * Checks if the disabled Question Gap DropDown.
   */
  const isDisabledQuestionGapDropDown = (): boolean =>
    selectedStandardQuestion &&
    selectedStandardQuestion?.QuestionType !== EnumQuestionTypesType.YesNo &&
    !selectedStandardQuestion.QuestionCategory;
  /**
   * Checks if the user has permission to show the draft button,
   * based on whether the question is ready to be reviewed.
   */
  const hasPermissionToShowDraftButton = (): boolean => true; //TODO- ADD PERMISSION

  /**
   * Indicates if the question is ready to be reviewed.
   */
  // const isReadyToReviewQuestion = (): boolean =>
  //   bulkStandardQuestionUploaderPageState?.BulkQuestionUploadPageMode ===
  //   EnumBulkQuestionUploadPageMode.ReviewQuestionMode;
  const isReadyToReviewQuestion = useMemo(() => {
    return (
      bulkStandardQuestionUploaderPageState?.BulkQuestionUploadPageMode ===
      EnumBulkQuestionUploadPageMode.ReviewQuestionMode
    );
  }, [bulkStandardQuestionUploaderPageState?.BulkQuestionUploadPageMode]);

  /**
   * Indicates if the question is ready to validate.
   */
  const isReadyToValidateQuestion = (): boolean =>
    bulkStandardQuestionUploaderPageState?.BulkQuestionUploadPageMode ===
    EnumBulkQuestionUploadPageMode.ValidateQuestionMode;

  /**
   * Determines whether the file uploader should be displayed.
   */
  // const isReadyToUploadQuestion = (): boolean =>
  //   bulkStandardQuestionUploaderPageState?.BulkQuestionUploadPageMode ===
  //   EnumBulkQuestionUploadPageMode.UploadQuestionMode;
  const isReadyToUploadQuestion = useMemo(() => {
    return (
      bulkStandardQuestionUploaderPageState?.BulkQuestionUploadPageMode ===
      EnumBulkQuestionUploadPageMode.UploadQuestionMode
    );
  }, [bulkStandardQuestionUploaderPageState?.BulkQuestionUploadPageMode]);

  /**
   * Checks if the user has permission to show the proceed button,
   */
  const hasPermissionToShowProceedButton = (): boolean =>
    bulkStandardQuestionUploaderPageState?.BulkQuestionUploadPageMode ===
      EnumBulkQuestionUploadPageMode.ValidateQuestionMode &&
    bulkStandardQuestionUploaderPageState?.StandardQuestionReviewList?.some(
      (some) => some.IsWarning === false
    );

  /**
   * enable multiple select dropdown muliple select,
   */
  const disableMultipleSelect = (): boolean =>
    selectedStandardQuestion?.QuestionType === EnumQuestionTypesType.YesNo ||
    selectedStandardQuestion?.QuestionType === EnumQuestionTypesType.RatingStar;

  /**
   * Resets the bulk upload state
   */
  const resetUploadState = () => {
    setSelectedStandardQuestion(null);
    dispatch(resetBulkQuestionUploadState());
  };

  /**
   * Handles the Back button click event.
   */
  const onClickBackButton = () => {
    // User is reviewing, may have unsaved changes — confirm before resetting
    if (isReadyToReviewQuestion) {
      confirmDiscardIfUnsavedChanges({
        isDirty: true,
        onConfirm: () => {
          resetUploadState();
          dispatch(
            setPageNavigation({
              EnablePageNavigation: true,
              NavigationPage: "/standardsQuestions",
            })
          );
        },
      });
      return;
    }

    // In validate mode —  reset state
    if (isReadyToValidateQuestion()) {
      confirmDiscardIfUnsavedChanges({
        isDirty: true,
        message:
          "Proceeding will clear all current requirements and take you back to a fresh upload screen. Would you like to continue?",
        onConfirm: () => {
          resetUploadState();
        },
      });
      return;
    }

    // Default case — reset and navigate
    resetUploadState();
    dispatch(
      setPageNavigation({
        EnablePageNavigation: true,
        NavigationPage: "/standardsQuestions",
      })
    );
  };

  /**
   * Resets the selected standard question to its original state from the review list.
   */
  const resetSelectedQuestionDetails = () => {
    const index = getSelectedStandardQuestionArrayIndex();

    if (index === -1) return;

    const selectedQuestionwithoutChange = {
      ...bulkStandardQuestionUploaderPageState?.StandardQuestionReviewList[
        index
      ],
    };

    setSelectedStandardQuestion(selectedQuestionwithoutChange);
  };

  /**
   *  Displays a confirmation popup if the currently selected question has unsaved changes.
   * If no changes are detected (IsDirty is false), the optional `yesBtnClick` is executed immediately.
   */
  const confirmDiscardIfUnsavedChanges = (props: {
    isDirty?: boolean;
    message?: string;
    onConfirm?: () => void;
  }) => {
    if (
      !(props.isDirty ?? false) &&
      (!selectedStandardQuestion ||
        (selectedStandardQuestion &&
          !(selectedStandardQuestion.IsDirty ?? false)))
    ) {
      props.onConfirm?.();
      return;
    }

    dispatch(
      openPopup({
        Open: true,
        BodyText:
          props?.message ??
          "You have some unsaved changes, are you sure you want to discard the changes?",
        HeaderText: "Confirmation",
        PopupType: EnumPopupType.YesNoConfirmation,
        YesBtnClick() {
          resetSelectedStandardQuestionState();
          props.onConfirm?.();
        },
        DisableBackdropClose: true,
      } as PopupProps)
    );
  };

  // page unload validations
  const prompt = {
    Message:
      "You have some unsaved changes, are you sure you want to discard the changes?",
    When:
      !bulkQuestionUploadState?.PageNavigation?.EnablePageNavigation &&
      isReadyToReviewQuestion,
    YesBtnClick() {
      dispatch(resetBulkQuestionUploadState());
    },
  } as UsePromptTypes;
  usePrompt(prompt);

  /**
   *  Handles file upload changes by dispatching an action to bulk upload a standard question.
   */
  const onChangeFileUploaderCallBack = (files: Array<UploadFileDTO>) => {
    // Proceed only if exactly one file is selected
    if (files?.length === 1) {
      const file = files[0] ?? ({} as UploadFileDTO);

      dispatch(setUploadedFileName(file?.FileName));

      dispatch(
        bulkUploadStandardQuestion({
          ...file,
          StandardId: parameter.StandardId,
        })
      );
    }
  };

  /**
   *  Handles the delete action callback when one or more questions are selected for deletion.
   */
  const handleDeleteQuestionCallBack = (rowIds: Array<string>) => {
    confirmDiscardIfUnsavedChanges({
      onConfirm() {
        dispatch(
          openPopup({
            Open: true,
            BodyText: `Are you sure you want to delete the selected question(s)?`,
            HeaderText: "Confirmation",
            PopupType: EnumPopupType.YesNoConfirmation,
            Size: "modal-md",
            YesBtnClick() {
              deleteSelectedStandardQuestions(rowIds);
            },
          } as PopupProps)
        );
      },
    });
  };

  /**
   *  delete selected standard question.
   */
  const deleteSelectedStandardQuestions = (rowIds: Array<string>) => {
    const { StandardQuestionReviewList: standardQuestionReviewList } =
      bulkStandardQuestionUploaderPageState;

    // Return early if there's no list to update
    if (standardQuestionReviewList?.length === 0) return;

    // Filter out the questions that match the given codes
    const updatedStandardQuestionList = [
      ...reOrderStandardQuestionReviewList([
        ...standardQuestionReviewList.filter(
          (filter) => !rowIds.includes(filter.RowId)
        ),
      ]),
    ];

    // If all questions are deleted, reset the entire bulk upload state
    if (updatedStandardQuestionList?.length === 0) {
      dispatch(resetBulkQuestionUploadState());
      setSelectedStandardQuestion(null);
      showSuccessPopup({ message: "Question(s) deleted successfully" });
      return;
    }
    // current selected question question code reset
    const selectedIndex = getSelectedStandardQuestionArrayIndex();

    // Proceed only if a valid selection exists
    if (selectedIndex !== -1) {
      const selectedCode = selectedStandardQuestion?.RowId;

      const isDeleted = rowIds?.includes(selectedCode);

      if (isDeleted) {
        // If the selected question was deleted, clear the selection
        setSelectedStandardQuestion(null);
      } else {
        // Count how many questions before the selected index were deleted
        const deletedCountBeforeSelected =
          standardQuestionReviewList?.filter(
            (question, index) =>
              rowIds.includes(question.RowId) && index < selectedIndex
          )?.length || 0;

        const adjustedIndex = selectedIndex - deletedCountBeforeSelected + 1;

        // Update the selected question with the adjusted index
        setSelectedStandardQuestion((prev) => ({
          ...prev,
          RowId: `${adjustedIndex}`,
        }));
      }
    }

    // Update uploader page state with new reordered list
    setBulkStandardQuestionUploaderPageState((prevState) => {
      return {
        ...prevState,
        StandardQuestionReviewList: [...updatedStandardQuestionList],
      };
    });

    // Update the review list and reset the selected question if needed
    dispatch(setStandardQuestionReviewList(updatedStandardQuestionList));

    showSuccessPopup({ message: "Question(s) deleted successfully" });
  };

  /**
   *Callback handler for when the question type dropdown value changes.
   */
  const onchangeQuestionTypeDropdownCallBack = (
    name: string,
    selectedValue: string
  ) => {
    // Reset dependent
    dispatch(setBulkUploadQuestionDropdownCategories([]));
    dispatch(setBulkUploadQuestionCategoryChoices([]));
    setSelectedStandardQuestion((prevState) => {
      return {
        ...prevState,
        QuestionType: 0,
        QuestionCategory: 0,
        QuestionChoices: [],
      };
    });

    if (!selectedValue) return;

    //convert selected value to numeric question type ID
    const questionTypeId = parseInt(selectedValue) || 0;

    // Handle question types that require dropdown categories
    switch (questionTypeId) {
      case EnumQuestionTypesType.YesNo:
        dispatch(
          setBulkUploadQuestionCategoryChoices([
            {
              ChoiceTextId: 1,
              ChoiceText: { DefaultText1: "Yes" } as DefaultTextDTO,
            } as QuestionChoiceDTO,
            {
              ChoiceTextId: 0,
              ChoiceText: { DefaultText1: "No" } as DefaultTextDTO,
            } as QuestionChoiceDTO,
          ])
        );
        break;
      case EnumQuestionTypesType.FreeText:
        break;
      default:
        dispatch(getBulkUploadQuestionDropdownCategories(questionTypeId));
        break;
    }

    // Update selected question type state
    setSelectedStandardQuestion((prevState) => {
      return {
        ...prevState,
        QuestionType: questionTypeId,
      };
    });
  };

  /**
   * Callback handler for when the question category dropdown value changes.
   */
  const onchangeQuestionCategoryDropdownCallBack = (
    name: string,
    selectedValue: string
  ) => {
    //Clear previous choices
    dispatch(setBulkUploadQuestionCategoryChoices([]));

    setSelectedStandardQuestion((prevState) => {
      return {
        ...prevState,
        QuestionCategory: 0,
        QuestionChoices: [],
      };
    });

    if (!selectedValue) return;

    // Convert selected value to integer ID
    const questionCategoryId = parseInt(selectedValue) ?? 0;

    //Dispatch action to fetch new choices for selected category
    dispatch(
      getBulkUploadQuestionCategoryChoices({
        QuestionCategoryId: questionCategoryId,
      } as QuestionDTO)
    );

    // Update selected category in state
    setSelectedStandardQuestion((prevState) => {
      return {
        ...prevState,
        QuestionCategory: questionCategoryId,
      };
    });
  };

  /**
   * Converts the current question's choices into a dropdown-compatible list.
   */
  const convertToQuestionChoisesDropdownList = (): Array<DropDownDTO> => {
    const { QuestionChoices } =
      selectedStandardQuestion ?? ({} as StandardQuestionUploadDTO);

    // Return empty array if no choices are available
    if (!QuestionChoices || QuestionChoices?.length === 0) return [];

    // Map each question choice to a dropdown-compatible item
    return [
      ...QuestionChoices?.map((choise) => {
        return {
          Value: choise.ChoiceTextId?.toString(),
          Label: choise.ChoiceText?.DefaultText1,
        } as DropDownDTO;
      }),
    ];
  };

  /**
   * * Retrieves a list of choice IDs (as strings) that are marked as "gaps"
   * from the currently selected standard question.
   */
  const getSelectedQuestionChoiseGapsCallBack = (): Array<string> => {
    const { QuestionChoices: questionChoices } =
      selectedStandardQuestion ?? ({} as StandardQuestionUploadDTO);

    // Return empty array if there are no choices
    if (!questionChoices || questionChoices?.length === 0) return [];
    // Filter choices marked as gaps and return their IDs as strings
    const updatedQuestionChoices =
      questionChoices
        ?.filter((filter) => filter.IsGap ?? false)
        ?.map((item) => {
          return item.ChoiceTextId?.toString();
        }) ?? [];

    return [...updatedQuestionChoices];
  };

  /**
   * Callback to update the `IsGap` flag for each question choice,
   * based on the currently selected gap choices.
   */
  const onSelectedQuestionChoiseGapChangeCallBack = (
    seletedQuestionChoiseGap: Array<string>
  ) => {
    const {
      QuestionChoices: questionChoices = [],
      QuestionType: questionType,
    } = selectedStandardQuestion ?? {};

    if (!questionChoices.length) return [];

    const selectedIds = seletedQuestionChoiseGap ?? [];
    const selectedLength = selectedIds.length;

    const originalIds = questionChoices.map((q) => q.ChoiceTextId?.toString());
    const previousSelectedIds = questionChoices
      .filter((q) => q.IsGap)
      .map((q) => q.ChoiceTextId?.toString());

    const { type, index } = findIndexOfSelectionChange(
      originalIds,
      previousSelectedIds,
      selectedIds
    );

    const updatedChoices = questionChoices.map((choice, i) => {
      const choiceId = choice.ChoiceTextId?.toString();

      let isGap = false;

      if (questionType === EnumQuestionTypesType.RatingStar) {
        isGap =
          selectedLength === questionChoices.length ||
          (selectedLength > 0 &&
            ((type === "add" && i <= index) ||
              (type === "removed" && i < index)));
      } else if (questionType === EnumQuestionTypesType.YesNo) {
        isGap = selectedLength > 0 && i === index;
      } else {
        isGap = selectedLength > 0 && selectedIds.includes(choiceId);
      }

      return { ...choice, IsGap: isGap };
    });

    setSelectedStandardQuestion((prevState) => ({
      ...prevState,
      QuestionChoices: [...updatedChoices],
    }));
  };

  /**
   * Callback handler for single-select dropdowns (boolean-based).
   */
  const handleBooleanDropdownChange = (name: string, selectedValue: string) => {
    // Update state with modified
    setSelectedStandardQuestion((prevState) => {
      return {
        ...prevState,
        [name]: parseToBoolean(selectedValue),
      };
    });
  };

  /**
   * check selcted question changes are valid
   */
  const isPageValid = (): boolean => {
    let isValid = bulkStandardQuestionUploaderPageState.Validator.allValid();
    if (!isValid) {
      //show validation message
      bulkStandardQuestionUploaderPageState.Validator.showMessages();
    } else {
      //hide validation message
      bulkStandardQuestionUploaderPageState.Validator.hideMessages();
    }

    return isValid;
  };

  /**
   * Checks if there is a duplicate requirement in the list of standard questions under review.
   */
  const isDuplicateRequermentExist = (): boolean => {
    // Get the current list of questions under review from page-level state
    let { StandardQuestionReviewList: standardQuestionReviewList } =
      bulkStandardQuestionUploaderPageState;

    return standardQuestionReviewList?.some(
      (some) =>
        selectedStandardQuestion?.RowId !== some.RowId &&
        some.Requirement?.trim() ===
          selectedStandardQuestion?.Requirement?.trim()
    );
  };

  /**
   *  Updates the currently selected standard question within the review list.
   */
  const saveSelectedStandardQuestion = () => {
    // check validation
    if (!isPageValid()) {
      forceUpdate();
      return;
    }

    // check duplicate requerment exist
    if (isDuplicateRequermentExist()) {
      showInvalidQuestionWarning({
        message:
          "This requirement already exists in the uploaded list. Please review and update the requirement.",
      });

      return;
    }

    // Destructure key properties from the currently selected question
    const { QuestionType: questionType, QuestionCategory: questionCategory } =
      selectedStandardQuestion;

    // Extract dropdown data used for label resolution from the reducer state
    const {
      BulkQuestionTypeList: questionTypeList,
      BulkQuestionCategoryDropdownList: questionCategoryList,
    } = bulkQuestionUploadState;

    // Get the current list of questions under review from page-level state
    let { StandardQuestionReviewList: standardQuestionReviewList } =
      bulkStandardQuestionUploaderPageState;

    // Initialize display name variables for type and category
    let questionTypeName = undefined;
    let questionCategoryName = undefined;

    // Find the index of the selected question in the review list
    const index = getSelectedStandardQuestionArrayIndex();

    // Exit early if no matching question is found
    if (index === -1) return;

    if (questionType > 0) {
      questionTypeName =
        questionTypeList?.find(
          (find) => find.Value?.toString() === questionType?.toString()
        )?.Label ?? undefined;
    }

    if (questionCategory > 0) {
      questionCategoryName =
        questionCategoryList?.find(
          (find) => find.Value?.toString() === questionCategory?.toString()
        )?.Label ?? undefined;
    }

    // Update the question in-place (mutation)
    let updatedStandardQuestionList = [...standardQuestionReviewList];
    updatedStandardQuestionList[index] = {
      ...selectedStandardQuestion,
      QuestionTypeName: questionTypeName,
      QuestionCategoryName: questionCategoryName,
      IsDirty: false,
      IsWarning: false,
      Warning: "",
    };

    //  updated review list into state
    updateStandardQuestionReviewListState(updatedStandardQuestionList);

    // set selected standard question state
    setSelectedStandardQuestion({ ...updatedStandardQuestionList[index] });
  };

  /**
   * Callback function to handle changes in a text box input.
   */
  const handleTextBoxChangeCallBack = (name: string, value: any) => {
    setSelectedStandardQuestion((prevState) => {
      return { ...prevState, [name]: value || "" };
    });
  };

  /**
   * Callback to handle when a standard question is selected.
   */
  const handleSelectedStandardQuestionChangeCallBack = (
    selectedQuestion: StandardQuestionUploadDTO
  ) => {
    confirmDiscardIfUnsavedChanges({
      onConfirm() {
        dispatch(setBulkUploadQuestionDropdownCategories([]));
        dispatch(setBulkUploadQuestionCategoryChoices([]));

        if (selectedQuestion.QuestionType ?? 0 !== 0) {
          switch (selectedQuestion.QuestionType) {
            case EnumQuestionTypesType.YesNo:
              dispatch(
                setBulkUploadQuestionCategoryChoices([
                  {
                    ChoiceTextId: 1,
                    ChoiceText: { DefaultText1: "Yes" } as DefaultTextDTO,
                  } as QuestionChoiceDTO,
                  {
                    ChoiceTextId: 0,
                    ChoiceText: { DefaultText1: "No" } as DefaultTextDTO,
                  } as QuestionChoiceDTO,
                ])
              );
              break;
            case EnumQuestionTypesType.FreeText:
              break;
            default:
              dispatch(
                getRowDataOnClick(
                  selectedQuestion.QuestionType,
                  {
                    QuestionCategoryId: selectedQuestion.QuestionCategory,
                  } as QuestionDTO,
                  selectedQuestion
                )
              );
              break;
          }

          // if (selectedQuestion.QuestionCategory ?? 0 !== 0) {
          //   dispatch(
          //     getBulkUploadQuestionCategoryChoices(
          //       {
          //         QuestionCategoryId: selectedQuestion.QuestionCategory,
          //       } as QuestionDTO,
          //       selectedQuestion
          //     )
          //   );
          // }
        }

        setSelectedStandardQuestion({ ...selectedQuestion });
      },
    });
  };

  /**
   * Handles opening of the copy popup for a specific question.
   */
  const handleCopyPopupOpen = (
    selectedOpenQuestion: StandardQuestionUploadDTO
  ) => {
    confirmDiscardIfUnsavedChanges({
      onConfirm() {
        resetSelectedQuestionDetails();
        setCopySelectedQuestion({ ...selectedOpenQuestion });
        setCopyPopupOpen(true);
      },
    });
  };

  /**
   * Handles closing of the copy popup
   */
  const onCopyPopupClose = () => {
    setCopyPopupOpen(false);
    setCopySelectedQuestion(null);
  };

  /**
   * Copies details from a selected question to other questions in the review list,
   */
  const handleCopyQuestionDetails = (
    applyAll: boolean,
    questionRange: Array<string>
  ) => {
    if (!applyAll && questionRange?.length === 0) return;

    // Get the current list of questions under review from page-level state
    const { StandardQuestionReviewList: standardQuestionReviewList } =
      bulkStandardQuestionUploaderPageState;

    const updatedStandardQuestionList = [
      ...standardQuestionReviewList?.map((question) => {
        if (
          question.RowId === copySelectedQuestion?.RowId ||
          (!applyAll && !questionRange?.includes(question.RowId))
        ) {
          return { ...question };
        }

        return {
          ...copySelectedQuestion,
          RowId: question.RowId,
          Requirement: question.Requirement,
        };
      }),
    ];
    // set selected question details
    if (
      selectedStandardQuestion &&
      questionRange?.includes(selectedStandardQuestion?.RowId)
    ) {
      setSelectedStandardQuestion({
        ...copySelectedQuestion,
        RowId: selectedStandardQuestion.RowId,
        Requirement: selectedStandardQuestion.Requirement,
      });
    }

    //  updated review list into state
    updateStandardQuestionReviewListState(updatedStandardQuestionList);

    dispatch(
      openPopup({
        Open: true,
        BodyText: "Details copied successfully",
        HeaderText: "Success",
        PopupType: EnumPopupType.SuccessPopup,
      } as PopupProps)
    );
  };

  /**
   * Retrieves a list of question codes from the review list that are available
   */
  const getAvailableRowIdForCopy = (): Array<string> => {
    const { StandardQuestionReviewList: standardQuestionReviewList } =
      bulkStandardQuestionUploaderPageState;

    return (
      standardQuestionReviewList
        ?.filter((filter) => filter.RowId !== copySelectedQuestion?.RowId)
        ?.map((item) => item.RowId) ?? []
    );
  };

  /**
   * Handles selection changes for questions in the review list.
   */
  const onChangeIsSelectedCallBack = (
    selectedQuestions: StandardQuestionUploadDTO[] | null,
    isAllClicked: boolean
  ) => {
    const { StandardQuestionReviewList: standardQuestionReviewList } =
      bulkStandardQuestionUploaderPageState;

    let updatedStandardQuestionList: Array<StandardQuestionUploadDTO> = [];

    if (selectedQuestions && selectedQuestions.length > 0) {
      // Create a quick lookup for performance
      const selectedMap = new Map(
        selectedQuestions.map((q) => [q.RowId, q.IsSelected ?? false])
      );

      // Update only the questions in the list
      updatedStandardQuestionList = standardQuestionReviewList.map(
        (question) => ({
          ...question,
          IsSelected: selectedMap.has(question.RowId)
            ? selectedMap.get(question.RowId)!
            : question.IsSelected ?? false,
        })
      );

      updateStandardQuestionReviewListState(updatedStandardQuestionList);
    }
    // else {
    //   // Select/Deselect all
    //   updatedStandardQuestionList = standardQuestionReviewList.map(
    //     (question) => ({
    //       ...question,
    //       IsSelected: isAllClicked ?? false,
    //     })
    //   );
    // }
  };

  /**
   * Handles the save action for uploaded standard questions.
   */
  const onClickSaveUploadedStandardQuestion = () => {
    confirmDiscardIfUnsavedChanges({
      onConfirm() {
        saveUploadedStandardQuestion();
      },
    });
  };

  /**
   * Saves the currently reviewed standard questions as a draft upload.
   */
  const saveUploadedStandardQuestion = () => {
    // Get the current list of questions under review from page-level state
    const {
      StandardId: standardId,
      StandardQuestionReviewList: standardQuestionReviewList,
    } = bulkStandardQuestionUploaderPageState;

    // Prepare the upload list by clearing the RowId for each question
    const uploadedStandardQuestion = [
      ...standardQuestionReviewList?.map((question) => {
        return { ...question, RowId: "" };
      }),
    ];

    // Dispatch the bulk save action with draft status
    dispatch(
      saveStandardQuestionBulkUpload({
        StandardId: standardId,
        QuestionList: uploadedStandardQuestion,
        SaveStatus: EnumStatus.Draft,
      } as StandardQuestionBulkUploadDTO)
    );
  };

  /**
   * Extracts all standard questions marked as warnings
   */
  const getStandardQuestionwithWarnings =
    (): Array<StandardQuestionUploadDTO> => {
      const { StandardQuestionReviewList } =
        bulkStandardQuestionUploaderPageState;

      // Filters the question list to include only items marked with IsWarning = true
      return [
        ...(StandardQuestionReviewList?.filter(
          (item) => item.IsWarning === true
        ) ?? []),
      ];
    };

  /**
   * Calculates the count of standard questions by category:
   * - Total number of questions
   * - Number of questions with warnings
   * - Number of valid (warning-free) questions
   */
  const calculateQuestionCount = (): BulkUploadQuestionCountType => {
    const { StandardQuestionReviewList } =
      bulkStandardQuestionUploaderPageState;
    // Get the list of questions that contain warnings
    const warnings = getStandardQuestionwithWarnings();

    // Count how many questions have warnings
    const warningQuestionCount = warnings?.length ?? 0;

    // Count the total number of reviewed questions
    const totalQuestionCount = StandardQuestionReviewList?.length ?? 0;

    // Compute the number of valid questions by subtracting warnings from total
    const validQuestionCount = totalQuestionCount - (warnings?.length ?? 0);

    // Return the result as an object
    return {
      TotalQuestionCount: totalQuestionCount,
      WarningQuestionCount: warningQuestionCount,
      ValidQuestionCount: validQuestionCount,
    } as BulkUploadQuestionCountType;
  };

  /**
   * Sends an email notification containing details of standard question issues (warnings).
   */
  const sendStandardQuestionIssueEmail = (
    bulkQuestionUploadPageMode: EnumBulkQuestionUploadPageMode
  ) => {
    const { StandardId: standardId } = bulkStandardQuestionUploaderPageState;

    const warnings = getStandardQuestionwithWarnings();

    //Update each warning with SequenceNumber and DisplayId
    const updatedWarnings = warnings.map((item) => ({
      ...item,
      SequenceNumber: item.DisplayId,
    }));

    const questionCount = calculateQuestionCount();
    if (questionCount.WarningQuestionCount === 0) return;

    // Construct the DTO object containing issue data and upload summary
    const issueSummary: StandardQuestionBulkUploadDTO = {
      StandardId: standardId,
      QuestionList: updatedWarnings,
      TotalQuestionCount: questionCount.TotalQuestionCount,
      ValidQuestionCount: questionCount.ValidQuestionCount,
      InvalidQuestionCount: questionCount.WarningQuestionCount,
      FileName: bulkQuestionUploadState.UploadedFileName,
    } as StandardQuestionBulkUploadDTO;

    // Dispatch the email notification action
    dispatch(
      notifyStandardQuestionIssuesByEmail(
        issueSummary,
        bulkQuestionUploadPageMode
      )
    );
  };

  /**
   * Handles the Do Not Proceed Button click event.
   */
  const onClickDoNotProceedButton = () => {
    dispatch(
      openPopup({
        Open: true,
        BodyText: `Uploaded question(s) will be canceled and not processed.
          <br/>Additionally, would you like the system to send an email with the identified error(s)?`,
        HeaderText: "Do Not Proceed - Email Confirmation",
        PopupType: EnumPopupType.YesNoConfirmation,
        showCloseButton: true,
        YesBtnClick() {
          sendStandardQuestionIssueEmail(
            EnumBulkQuestionUploadPageMode.UploadQuestionMode
          );
        },
        NoBtnClick() {
          resetUploadState();
        },
      } as PopupProps)
    );
  };

  /**
   * Handles the Proceed Button click event.
   */
  const onClickProceedButton = () => {
    dispatch(
      openPopup({
        Open: true,
        BodyText: `Important: Only valid question(s) will be processed.
          <br/>We'll send you an email with the identified error(s). Would you like to proceed?`,
        HeaderText: "Confirmation",
        PopupType: EnumPopupType.YesNoConfirmation,
        YesButtonText: "Proceed",
        NoButtonText: "Cancel",
        YesBtnClick() {
          sendStandardQuestionIssueEmail(
            EnumBulkQuestionUploadPageMode.ReviewQuestionMode
          );
        },
      } as PopupProps)
    );
  };

  const handleDragOverOutside = (e: any) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "none";
  };

  const handleDragOver = (e: any) => {
    e.preventDefault();
    setIsDragOver(true);
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const questionsData = useMemo(() => {
    return (
      bulkStandardQuestionUploaderPageState?.StandardQuestionReviewList ?? []
    );
  }, [bulkStandardQuestionUploaderPageState?.StandardQuestionReviewList]);

  const selectedRowId = useMemo(() => {
    return selectedStandardQuestion?.RowId ?? null;
  }, [selectedStandardQuestion?.RowId]);

  const questionsTable = useMemo(() => {
    return (
      <QuestionsTable
        QuestionsData={questionsData}
        onChangeSelectRow={handleSelectedStandardQuestionChangeCallBack}
        onChangeIsSelected={onChangeIsSelectedCallBack}
        onChangeDelete={handleDeleteQuestionCallBack}
        onCopyPopupOpen={handleCopyPopupOpen}
        SelectedRowId={selectedRowId}
      />
    );
  }, [questionsData, selectedRowId]);

  return (
    <div
      className="content-section-card top-main-padding"
      style={{
        height: "calc(100vh - 95px)",
        display: "flex",
        flexDirection: "column",
        overflowY: "hidden",
        overflowX: "auto",
      }}
      onDragOver={handleDragOverOutside}
      onDragLeave={handleDragLeave}
    >
      {copyPopupOpen && (
        <CopyDetailsPopup
          open={copyPopupOpen}
          onClose={onCopyPopupClose}
          sourceQuestion={
            {
              RowId: copySelectedQuestion?.RowId,
              Requirement: copySelectedQuestion?.Requirement,
              QuestionType: copySelectedQuestion?.QuestionType,
              QuestionTypeName: copySelectedQuestion?.QuestionTypeName,
              QuestionChoices: copySelectedQuestion?.QuestionChoices,
              QuestionCategory: copySelectedQuestion?.QuestionCategory,
              QuestionCategoryName: copySelectedQuestion?.QuestionCategoryName,
              IncludeNotApplicable:
                copySelectedQuestion?.IncludeNotApplicable == true
                  ? true
                  : copySelectedQuestion?.IncludeNotApplicable == false
                  ? false
                  : null,
              AllowComments:
                copySelectedQuestion?.AllowComments == true
                  ? true
                  : copySelectedQuestion?.AllowComments == false
                  ? false
                  : null,
            } as StandardQuestionUploadDTO
          }
          RowIdList={getAvailableRowIdForCopy()}
          onHandleCopy={handleCopyQuestionDetails}
        />
      )}

      <Grid
        container
        className=" flex-wrap_wrap"
        onDragOver={handleDragOverOutside}
        onDragLeave={handleDragLeave}
      >
        {/* Header with Search */}
        <div
          style={{ paddingBottom: "10px" }}
          className="flex--container width100-p top-main-border mb-5"
          onDragOver={handleDragOverOutside}
          onDragLeave={handleDragLeave}
        >
          <div className="flex__item--inherit pr-10 pl-10">
            <h2>
              <Tooltip title="Back to home page">
                <Button
                  aria-label="back"
                  className="sub-head-back-btn"
                  onClick={onClickBackButton}
                >
                  <ArrowBackIosNewOutlinedIcon className="" />
                  <span className="body-bold secondary-color">Back</span>
                </Button>
              </Tooltip>
            </h2>
          </div>

          <div className="d-flex width100-p flex-wrap_wrap">
            <div className="d-flex">
              {parameter.StandardName && (
                <h1
                  className="page_title d-flex tab-view-fnt-resize mr-0"
                  // title={parameter.StandardName}
                >
                  <Tooltip title={parameter.StandardName}>
                    <span className="block no-wrap limit-1 long-title-truncate">
                      Standard: {parameter.StandardName}
                    </span>
                  </Tooltip>
                  <span className="d-flex pl-10 pr-10 fx-align-center arrow-separator">
                    {" "}
                    <ArrowRight2
                      color="#000"
                      style={{ paddingTop: "5px", marginTop: "-10px" }}
                      size="24"
                    />
                  </span>
                </h1>
              )}

              {isReadyToValidateQuestion() && (
                <>
                  <h1 className="page_title no-wrap tab-view-fnt-resize">
                    Bulk Upload (Validation)
                  </h1>
                </>
              )}
              {/* Show or hide this title */}
              {isReadyToUploadQuestion && (
                <div className="">
                  <h1 className="page_title tab-view-fnt-resize">
                    Bulk Upload
                  </h1>
                </div>
              )}
              {/* Show or hide this title */}
              {isReadyToReviewQuestion && (
                <>
                  <h1 className="page_title d-flex no-wrap limit-1 tab-view-fnt-resize">
                    Bulk Upload (Review)
                  </h1>
                </>
              )}
            </div>
            <div className="ml-auto">
              {isReadyToValidateQuestion() && (
                <>
                  {/* <h1 className="page_title no-wrap">
                    Bulk Upload (Validation)
                  </h1> */}
                  {/* Action Buttons */}
                  <div className="ml-auto">
                    <Tooltip title="Do Not Proceed">
                      <Button
                        aria-label="Do Not Proceed"
                        className="secondary-btn-small"
                        variant="outlined"
                        onClick={onClickDoNotProceedButton}
                        startIcon={<CloseCircle variant="Outline" />}
                      >
                        <span> Do Not Proceed</span>
                      </Button>
                    </Tooltip>

                    {hasPermissionToShowProceedButton() && (
                      <Tooltip title="Proceed">
                        <Button
                          style={{ paddingRight: "15px" }}
                          aria-label="Proceed"
                          className="primary-btn-small"
                          onClick={onClickProceedButton}
                        >
                          <ArrowCircleRight
                            style={{ marginRight: "5px" }}
                            size="32"
                          />{" "}
                          <span>Proceed</span>
                        </Button>
                      </Tooltip>
                    )}
                  </div>
                </>
              )}
              {isReadyToReviewQuestion && hasPermissionToShowDraftButton() && (
                <div style={{ marginLeft: "auto" }}>
                  <Button
                    variant="contained"
                    className="primary-btn-small mr-10"
                    style={{ height: "38px" }}
                    startIcon={<ClipboardText />}
                    onClick={onClickSaveUploadedStandardQuestion}
                  >
                    Save to Draft
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </Grid>

      {/* Main Content with Splitter */}
      <div
        className="pl-10 pr-10"
        style={{
          flex: "1 1 auto",
          borderRadius: "4px",
          overflowX: "hidden",
          padding: "15px 10px 15px 0",
          overflowY: "hidden",
          minHeight: 0,
        }}
        onDragOver={isDragOver ? handleDragOver : handleDragOverOutside}
        onDragLeave={handleDragLeave}
      >
        {/* Standard Question validation section */}
        {isReadyToValidateQuestion() && (
          <div className="validation-questions-container">
            <BulkUploadValidateQuestion
              StandardQuestionsWithWarnings={getStandardQuestionwithWarnings()}
              UploadedFileName={bulkQuestionUploadState?.UploadedFileName ?? ""}
              QuestionCount={calculateQuestionCount()}
            />
          </div>
        )}

        {(isReadyToUploadQuestion || isReadyToReviewQuestion) && (
          <>
            {/* Upload Mode - Full Screen */}
            {isReadyToUploadQuestion && (
              <div style={{ height: "100%", width: "100%" }}>
                <HPRFileUploaderWithNavigation
                  Label={"Bulk Question Uploader"}
                  FileType={[EnumFileType.Excel]}
                  Name={"bulkQuestionUploader"}
                  onChangeFileUploader={onChangeFileUploaderCallBack}
                />
              </div>
            )}

            {/* Review Mode - With Splitter */}
            {isReadyToReviewQuestion && (
              <Splitter
                style={{
                  height: "100%",
                  minWidth: "100%",
                }}
              >
                <Splitter.Panel defaultSize={"70%"} min={"50%"} max={"90%"}>
                  <div
                    style={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                      position: "relative",
                    }}
                  >
                    <div style={{ flex: 1, minHeight: 0 }}>
                      {/* <QuestionsTable
                        QuestionsData={questionsData}
                        onChangeSelectRow={
                          handleSelectedStandardQuestionChangeCallBack
                        }
                        onChangeIsSelected={onChangeIsSelectedCallBack}
                        onChangeDelete={handleDeleteQuestionCallBack}
                        onCopyPopupOpen={handleCopyPopupOpen}
                        SelectedRowId={selectedRowId}
                      /> */}
                      {questionsTable}

                      {bulkStandardQuestionUploaderPageState?.StandardQuestionReviewList?.some(
                        (item) => item.IsWarning === true
                      ) &&
                        (() => {
                          const warnings =
                            bulkStandardQuestionUploaderPageState?.StandardQuestionReviewList?.filter(
                              (item) => item.IsWarning === true
                            )?.map((item) => `${item.SequenceNumber}`);

                          return (
                            <div className="draft-alert-container">
                              <div className="draft-alert-banner">
                                {/* Info Icon */}
                                <InfoCircle size={20} color="#FF8A65" />

                                {/* Alert Message */}
                                <div className="draft-alert-message-wrapper">
                                  Could not complete Save to Draft action -
                                  <span className="draft-alert-warning-text">
                                    {warnings.length} warnings:{" "}
                                    {warnings.join(", ")}
                                  </span>
                                </div>

                                {/* Close Button */}
                                <button className="draft-alert-close-btn">
                                  ×
                                </button>
                              </div>
                            </div>
                          );
                        })()}
                    </div>
                  </div>
                </Splitter.Panel>

                <Splitter.Panel>
                  {/* Right Panel - Table Details Form */}
                  {selectedStandardQuestion && (
                    <div
                      className="table-details-container"
                      style={{ backgroundColor: "#F9FAFB" }}
                    >
                      <div
                        style={{ flexDirection: "column" }}
                        className="table-details-wrapper d-flex fx-between"
                      >
                        <div>
                          {/* Header */}
                          <h5 className="table-details-header">
                            Table Details
                          </h5>

                          {/* QID Field */}
                          <div className="form-group">
                            <HPRTextBox
                              ClassName="table-details-field"
                              Id="RID"
                              Label="ID"
                              Name="RID"
                              Value={selectedStandardQuestion?.RowId ?? ""}
                              Type={EnumTextBoxType.Text}
                              Disabled={true}
                            />
                          </div>

                          {/* Requirements Field */}
                          <div className="form-group mb-10">
                            <HPRTextBoxAutoResize
                              Id="Requirement"
                              Label="Requirement"
                              Name="Requirement"
                              Type={EnumTextBoxType.Text}
                              Value={
                                selectedStandardQuestion?.Requirement ?? ""
                              }
                              onTextBoxChange={(
                                name: string,
                                value: string
                              ) => {
                                handleTextBoxChangeCallBack(name, value);
                              }}
                              Rules="required"
                              IsEnableValidator
                              Required
                              Disabled={false}
                              InputProps={{ maxLength: 2000 }}
                              TextLength={2000}
                              ShowTextCounter
                              Validator={
                                bulkStandardQuestionUploaderPageState.Validator
                              }
                            />
                          </div>

                          {/* Type Dropdown */}
                          <div className="form-group">
                            <HPRDropDownSingleSelect
                              Id="Type"
                              Label="Type"
                              Name="QuestionType"
                              Items={
                                bulkQuestionUploadState?.BulkQuestionTypeList ??
                                []
                              }
                              Select={
                                selectedStandardQuestion?.QuestionType ?? 0
                              }
                              onDropDownChange={(_, selectedValue, name) => {
                                onchangeQuestionTypeDropdownCallBack(
                                  name,
                                  selectedValue
                                );
                              }}
                            />
                          </div>

                          {!isFreeTextQuestionSelected() && (
                            <>
                              {/* Choice Dropdown */}
                              {!isYesNoQuestionSelected() && (
                                <div className="form-group">
                                  <HPRDropDownSingleSelect
                                    Id="Choice"
                                    Label="Choice"
                                    Name="QuestionCategory"
                                    Items={
                                      bulkQuestionUploadState?.BulkQuestionCategoryDropdownList ??
                                      []
                                    }
                                    Select={
                                      selectedStandardQuestion?.QuestionCategory ??
                                      0
                                    }
                                    onDropDownChange={(
                                      _,
                                      selectedValue,
                                      name
                                    ) => {
                                      onchangeQuestionCategoryDropdownCallBack(
                                        name,
                                        selectedValue
                                      );
                                    }}
                                    Disabled={isDisabledQuestionCategoryDropDown()}
                                    IsSearchable
                                  />
                                </div>
                              )}

                              {/* Gap Multi-Select Dropdown */}
                              <div style={{ marginBottom: "20px" }}>
                                <MultipleSelectorWithSearch
                                  Label="Gap(s)"
                                  Disabled={isDisabledQuestionGapDropDown()}
                                  Options={convertToQuestionChoisesDropdownList()}
                                  SelectedValueArray={[
                                    ...getSelectedQuestionChoiseGapsCallBack(),
                                  ]}
                                  onSelectionChange={(
                                    selected: Array<string>
                                  ) => {
                                    onSelectedQuestionChoiseGapChangeCallBack(
                                      selected
                                    );
                                  }}
                                  DisableMultipleSelect={disableMultipleSelect()}
                                  ButtonLabel="Close"
                                />
                              </div>
                            </>
                          )}

                          {/* Include not applicable */}
                          <div className="form-group">
                            <HPRDropDownSingleSelect
                              Id="IncludeNotApplicable"
                              Label="Include Not Applicable"
                              Name="IncludeNotApplicable"
                              Select={booleanToNumber(
                                selectedStandardQuestion?.IncludeNotApplicable
                              )}
                              onDropDownChange={(_, selectedValue, name) => {
                                handleBooleanDropdownChange(
                                  name,
                                  selectedValue
                                );
                              }}
                              Items={[
                                { Value: "1", Label: "Yes" } as DropDownDTO,
                                { Value: "0", Label: "No" } as DropDownDTO,
                              ]}
                            />
                          </div>

                          {/* Allow comments */}
                          <div className="form-group">
                            <HPRDropDownSingleSelect
                              Id="AllowComments"
                              Label="Allow Comments"
                              Name="AllowComments"
                              Select={booleanToNumber(
                                selectedStandardQuestion?.AllowComments
                              )}
                              onDropDownChange={(_, selectedValue, name) => {
                                handleBooleanDropdownChange(
                                  name,
                                  selectedValue
                                );
                              }}
                              Items={[
                                { Value: "1", Label: "Yes" } as DropDownDTO,
                                { Value: "0", Label: "No" } as DropDownDTO,
                              ]}
                            />
                          </div>
                        </div>

                        {/* Save Button */}
                        <div
                          style={{ marginTop: "auto" }}
                          className="table-details-save-container"
                        >
                          <Button
                            variant="contained"
                            className="primary-btn-small mb-10"
                            onClick={(event) => {
                              event.stopPropagation();
                              saveSelectedStandardQuestion();
                            }}
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Right Panel - Records Display */}
                  {!selectedStandardQuestion && (
                    <div
                      style={{
                        padding: "40px",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#F9FAFB",
                      }}
                    >
                      <Box
                        sx={{
                          textAlign: "center",
                          color: "#666",
                          padding: "40px",
                        }}
                      >
                        {isReadyToReviewQuestion && (
                          <MuiTypography
                            variant="body2"
                            sx={{ marginBottom: "8px" }}
                          >
                            Select a question to view its details
                          </MuiTypography>
                        )}

                        {!isReadyToReviewQuestion && (
                          <MuiTypography variant="body2" sx={{}}>
                            <div className="body3">No Records to display</div>
                          </MuiTypography>
                        )}
                      </Box>
                    </div>
                  )}
                </Splitter.Panel>
              </Splitter>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BulkStandardQuestionUploader;
